import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Icon } from '@iconify/react';
import { movieApi } from '../api/client';
import { useSubmitMovieEdit } from '../hooks/useMovie';
import { getApiErrorMessage } from '../api/errors';
import { Button } from './ui/button';
import { SearchableSelect } from './ui/searchable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
} from './ui/dialog';
import { cn } from '../lib/utils';
import { checkImageRatio, POSTER_SPEC, THUMBNAIL_SPEC } from '../lib/image-ratio';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

const RATING_OPTIONS = ['G', 'PG', 'PG-13', 'R18', '12', '12A', '15', '18', 'NR'];
const CLASSIFICATION_AUTHORITY_OPTIONS = ['NFVCB', 'MPA', 'BBFC', 'Other'];
const CONTENT_DESCRIPTOR_OPTIONS = [
  'Violence', 'Strong Violence', 'Language', 'Sexual Content', 'Nudity',
  'Drugs', 'Alcohol', 'Horror', 'Frightening Scenes',
];

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
        {label}
        {hint && <span className="ml-1 font-normal text-muted-foreground/60">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

// Preset chips + free-text extras, flattened to the comma-separated string the API stores.
const descriptorString = ({ presets, custom }) =>
  [...presets, ...custom.split(',').map((s) => s.trim()).filter(Boolean)].join(',');

/**
 * A content creator's "edit my title" form. Nothing here touches the live title: submitting sends
 * only the fields that actually changed to POST /api/MovieApi/SubmitMovieEdit, where they sit as a
 * pending request until an admin approves (or rejects) them.
 *
 * `movie` is a row from useMyMovies() (MyMovieDto) — it carries everything needed to prefill.
 */
export function RequestTitleEditModal({ movie, onClose }) {
  const submitEdit = useSubmitMovieEdit();

  // Same query key/shape the Home page uses for the public category list.
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data } = await movieApi.getCategories();
      return data.success ? data.data || [] : [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const initial = useMemo(() => {
    const descriptors = movie.ContentDescriptors || [];
    return {
      title: movie.Title || '',
      description: movie.Description || '',
      director: movie.Director || '',
      cast: movie.Cast || '',
      releaseYear: movie.ReleaseYear ?? '',
      categoryId: movie.CategoryId ?? '',
      isKids: !!movie.IsKidsContent,
      rating: movie.Rating || '',
      authority: movie.ClassificationAuthority || 'NFVCB',
      presets: descriptors.filter((d) => CONTENT_DESCRIPTOR_OPTIONS.includes(d)),
      custom: descriptors.filter((d) => !CONTENT_DESCRIPTOR_OPTIONS.includes(d)).join(', '),
    };
  }, [movie]);

  const [form, setForm] = useState(initial);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [trailerFile, setTrailerFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleDescriptor = (d) =>
    set({ presets: form.presets.includes(d) ? form.presets.filter((x) => x !== d) : [...form.presets, d] });

  const handleThumb = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbnailFile(file);
    checkImageRatio(file, THUMBNAIL_SPEC).then((r) => {
      if (!r.ok) toast(r.message, { icon: '⚠️', duration: 7000 });
    });
    setThumbPreview(URL.createObjectURL(file));
  };

  const handlePoster = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPosterFile(file);
    checkImageRatio(file, POSTER_SPEC).then((r) => {
      if (!r.ok) toast(r.message, { icon: '⚠️', duration: 7000 });
    });
    setPosterPreview(URL.createObjectURL(file));
  };

  // Only what differs from the current title is sent, so the reviewer sees a clean diff.
  const buildPayload = () => {
    const payload = {};
    if (form.title.trim() !== initial.title) payload.title = form.title.trim();
    if (form.description !== initial.description) payload.description = form.description;
    if (form.director !== initial.director) payload.director = form.director;
    if (form.cast !== initial.cast) payload.cast = form.cast;
    // Year and category can be changed but not blanked out (the API reads "missing" as "unchanged").
    if (form.releaseYear !== '' && form.releaseYear !== initial.releaseYear) payload.releaseYear = form.releaseYear;
    if (form.categoryId !== '' && form.categoryId !== initial.categoryId) payload.categoryId = form.categoryId;
    if (form.isKids !== initial.isKids) payload.isKidsContent = form.isKids;
    if (form.rating !== initial.rating) payload.rating = form.rating;
    if (form.authority !== initial.authority) payload.classificationAuthority = form.authority;
    if (descriptorString(form) !== descriptorString(initial)) payload.contentDescriptors = descriptorString(form);
    if (thumbnailFile) payload.thumbnailFile = thumbnailFile;
    if (posterFile) payload.posterFile = posterFile;
    if (trailerFile) payload.trailerFile = trailerFile;
    return payload;
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Title is required.'); return; }

    const payload = buildPayload();
    if (Object.keys(payload).length === 0) { toast.error('Change something first.'); return; }

    setUploadProgress(0);
    try {
      const { data } = await submitEdit.mutateAsync({
        movieId: movie.Id,
        payload,
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      if (data.success) {
        toast.success(data.message || 'Edit submitted for review.');
        onClose();
      } else {
        toast.error(data.message || 'Could not submit that edit.');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not submit that edit.'));
    } finally {
      setUploadProgress(0);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon icon="ph:pencil-simple-bold" />
            Request edit
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="gap-6">
          <div className="flex items-start gap-2.5 rounded-lg border border-primary/25 bg-primary/[0.07] px-3.5 py-3 text-xs text-primary">
            <Icon icon="ph:info-bold" className="mt-0.5 size-4 shrink-0" />
            <span>
              Your changes are reviewed by an admin before they go live. Until then
              &ldquo;{movie.Title}&rdquo; stays exactly as it is.
            </span>
          </div>

          <Field label="Title">
            <input className={inputClass} value={form.title} onChange={(e) => set({ title: e.target.value })} />
          </Field>

          <Field label="Description">
            <textarea
              className={`${inputClass} resize-none`}
              rows={3}
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Director">
              <input className={inputClass} value={form.director} onChange={(e) => set({ director: e.target.value })} />
            </Field>
            <Field label="Release Year">
              <input
                type="number"
                className={inputClass}
                value={form.releaseYear}
                onChange={(e) => set({ releaseYear: e.target.value ? parseInt(e.target.value, 10) : '' })}
              />
            </Field>
          </div>

          <Field label="Cast" hint="(comma-separated)">
            <input className={inputClass} value={form.cast} onChange={(e) => set({ cast: e.target.value })} />
          </Field>

          <Field label="Category">
            <SearchableSelect
              value={form.categoryId ? String(form.categoryId) : ''}
              onValueChange={(value) => set({ categoryId: value ? parseInt(value, 10) : '' })}
              options={categories.map((c) => ({ value: String(c.Id), label: c.Name }))}
              placeholder="— Select category —"
              searchPlaceholder="Search categories…"
              emptyText="No categories found."
            />
          </Field>

          <Field label="Content Rating">
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.rating} onValueChange={(rating) => set({ rating })}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="— Select rating —" />
                </SelectTrigger>
                <SelectContent>
                  {RATING_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={form.authority} onValueChange={(authority) => set({ authority })}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLASSIFICATION_AUTHORITY_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Field>

          <Field label="Content Descriptors">
            <div className="flex flex-wrap gap-2">
              {CONTENT_DESCRIPTOR_OPTIONS.map((d) => {
                const active = form.presets.includes(d);
                return (
                  <button
                    type="button"
                    key={d}
                    onClick={() => toggleDescriptor(d)}
                    className={cn(
                      'rounded-full border px-3.5 py-1.5 text-xs transition-colors',
                      active
                        ? 'border-primary/60 bg-primary/15 text-primary'
                        : 'border-white/15 bg-white/[0.02] text-muted-foreground hover:border-white/30'
                    )}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
            <input
              className={`${inputClass} mt-2`}
              placeholder="Other descriptors (comma-separated) — not limited to the list above"
              value={form.custom}
              onChange={(e) => set({ custom: e.target.value })}
            />
          </Field>

          <label className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground/90">
            <input
              type="checkbox"
              checked={form.isKids}
              onChange={(e) => set({ isKids: e.target.checked })}
              className="size-4 accent-[var(--primary)]"
            />
            Suitable for Kids profiles
          </label>

          <Field label="Poster" hint="(portrait 2:3 — shown in rows & grids; leave blank to keep current)">
            <div className="flex items-center gap-3">
              {(posterPreview || movie.PosterUrl) && (
                <img
                  src={posterPreview || movie.PosterUrl}
                  alt="Poster"
                  className="h-[90px] w-[60px] shrink-0 rounded-md object-cover"
                />
              )}
              <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
                <Icon icon="ph:image-bold" className="size-4 shrink-0" />
                <span className="truncate">{posterFile ? posterFile.name : 'Choose portrait poster…'}</span>
                <input type="file" accept="image/*" onChange={handlePoster} className="hidden" />
              </label>
            </div>
          </Field>

          <Field label="Thumbnail" hint="(landscape 16:9 — hero & title page; leave blank to keep current)">
            <div className="flex items-center gap-3">
              {(thumbPreview || movie.ThumbnailUrl) && (
                <img
                  src={thumbPreview || movie.ThumbnailUrl}
                  alt="Thumbnail"
                  className="h-[54px] w-[90px] shrink-0 rounded-md object-cover"
                />
              )}
              <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
                <Icon icon="ph:image-bold" className="size-4 shrink-0" />
                <span className="truncate">{thumbnailFile ? thumbnailFile.name : 'Choose image…'}</span>
                <input type="file" accept="image/*" onChange={handleThumb} className="hidden" />
              </label>
            </div>
          </Field>

          <Field label="Trailer" hint="(leave blank to keep current)">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
              <Icon icon="ph:film-slate-bold" className="size-4 shrink-0" />
              <span className="truncate">{trailerFile ? trailerFile.name : 'Choose video file…'}</span>
              <input type="file" accept="video/*" onChange={(e) => setTrailerFile(e.target.files?.[0] || null)} className="hidden" />
            </label>
          </Field>

          {submitEdit.isPending && (
            <div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Finishing up…'}
              </p>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose} className="gap-1.5">
            <Icon icon="ph:x-bold" />
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitEdit.isPending} className="gap-1.5">
            <Icon icon="ph:paper-plane-tilt-bold" />
            {submitEdit.isPending ? 'Submitting…' : 'Submit for review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RequestTitleEditModal;
